import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthService from '../../services/auth-service';
import { Button } from './button';

export function ProfilePage() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Redirect to home if not authenticated
      navigate('/');
      return;
    }
    
    // Get user data from AuthService
    const currentUser = AuthService.getUser();
    if (currentUser) {
      setUser(currentUser);
    }
    setLoading(false);
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">You need to be logged in to view this page.</p>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <header className="w-full z-50 fixed top-0 left-0 px-4 py-3">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-between w-full h-14 rounded-xl bg-background/80 backdrop-blur-lg border border-border/60 shadow-sm px-6 transition-all duration-300">
            <div className="flex items-center">
              <div className="font-bold text-xl font-serif">Caude</div>
            </div>
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="sm" 
                asChild
              >
                <Link to="/access-codes">Access Codes</Link>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                asChild
              >
                <Link to="/">Home</Link>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Your Profile</h1>
            <p className="text-lg text-muted-foreground">Manage your account information and settings</p>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-sm mb-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-3xl font-bold text-primary">
                    {user.name?.charAt(0) || user.username?.charAt(0) || user.email?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1 w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">Name</label>
                    <div className="text-foreground font-medium">
                      {user.name || user.displayName || user.username || 'Not provided'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                    <div className="text-foreground font-medium">
                      {user.email || 'Not provided'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">User ID</label>
                    <div className="text-foreground font-medium font-mono text-sm">
                      {user.id || user.userId || 'Not available'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">Authentication Provider</label>
                    <div className="text-foreground font-medium capitalize">
                      {user.provider || 'Email'}
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Account Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-muted rounded-lg p-4">
                      <h4 className="font-medium text-foreground mb-2">Security</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Manage your password and security settings
                      </p>
                      <Button variant="outline" size="sm" disabled>
                        Update Security
                      </Button>
                    </div>

                    <div className="bg-muted rounded-lg p-4">
                      <h4 className="font-medium text-foreground mb-2">Notifications</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Configure how you receive updates
                      </p>
                      <Button variant="outline" size="sm" disabled>
                        Manage Notifications
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Activation Codes - REMOVED FROM HERE */}
          {/* We've moved this to its own dedicated page */}
          
          <div className="text-center">
            <Button variant="outline" asChild>
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;